import React, { useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import pluginPages from '../../pluginPages';
import { usePlugin } from '../hooks/usePlugins';
import { resolvePluginPage, tildeDecode } from '../definitions';
import { componentFor } from '../pageLoading';

/**
 * Every plugin page in the CMS, from one route.
 *
 * This is the whole replacement for `pluginWrapperController` and the 134 Angular bridge
 * templates it used to `ng-include`. The controller looked the plugin up in `plugins.json`,
 * put `pageName`, `params` and `splitParams` on the scope, and let the template name a
 * component and pick values off that scope. All of that is now data: `pluginPages.js` names
 * each page's file and its positional arguments, and the page loads on first open, its code
 * already downloading from when the route matched (`../pageLoading`).
 *
 * The redirect behaviour is carried over, including its distinction between a plugin with no
 * pages at all, which is a server-only plugin like `page-manager` and a stale bookmark rather
 * than a fault, and a genuine miss, which is a broken link worth an error in the log.
 */
const PluginPage = () => {
  const raw = useParams({ strict: false });
  // ui-router escaped `/` as `~2F` in path segments, so three plugin names and every
  // `directories` deep link arrive tilde-escaped. See `tildeDecode`.
  const pluginName = tildeDecode(raw.pluginName);
  const { pageName, params } = raw;
  const { plugin, loading, failed } = usePlugin(pluginName);
  const navigate = useNavigate();

  const resolved =
    plugin &&
    resolvePluginPage({
      plugin,
      pluginName,
      pageName,
      params,
      pages: pluginPages,
    });

  /*
   * Every miss goes home, which is what `pluginWrapperController` did with
   * `$location.path('/').replace()` in all three of its failure branches. Rendering nothing
   * instead would leave an admin on a stale bookmark staring at an empty content area with
   * the rail still highlighted, and no way to tell whether the page was slow or gone.
   *
   * `replace`, so the broken URL does not become a back-button trap.
   */
  const missing = !loading && (failed || !plugin || !resolved);
  useEffect(() => {
    if (missing) navigate({ to: '/main/', replace: true });
  }, [missing, navigate]);

  // Nothing renders while the plugin list is in flight, as `ng-include` had no template
  // until `getPlugin` resolved.
  if (loading || !resolved) return null;

  const Component = componentFor(resolved.entry);
  const { props } = resolved;
  /*
   * Keyed on the full state identity, because ui-router remounted the page on every param
   * change and a shared route component does not.
   *
   * `main.plugin` declared no `dynamic` params, so ui-router re-entered the state whenever
   * `pluginName`, `pageName` or `params` changed. Re-entry built a new `pluginWrapperController`
   * and a new `<div ng-include="template">`, which tore the React component down and mounted a
   * fresh one. Every plugin page was written against that guarantee.
   *
   * TanStack keeps this component mounted across such a change and only updates `useParams`,
   * so without a key the same element type renders with new props and all of its `useState`
   * survives. The flow that exposes it is the common one: an editor opened at `.../new` saves
   * and then routes to `.../<id>` (`useManageGroupActions`, `useCollectionListActions`,
   * `useAchievementActions`, `useCalendarEventsActions` and the navbar hooks all do this), which
   * would leave the just-submitted form state in place over the saved record.
   *
   * Identical params produce an identical key and therefore no remount, which is also what
   * ui-router did: `$state.go` to the current state and params was a no-op without `reload`.
   */
  return <Component key={`${pluginName}#${pageName}#${params ?? ''}`} {...props} />;
};

export default PluginPage;
